import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Briefcase, Eye, EyeOff, Mail, Lock, AlertTriangle, Send, ArrowLeft, User, Smartphone } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LoginPage = ({ onLogin }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loginType, setLoginType] = useState('email'); // 'email' or 'username'
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // OTP states
  const [otpStep, setOtpStep] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpUserId, setOtpUserId] = useState('');
  const [otpLoginToken, setOtpLoginToken] = useState('');
  const [otpMaskedPhone, setOtpMaskedPhone] = useState('');
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [otpResendCooldown, setOtpResendCooldown] = useState(0);
  const [rememberDevice, setRememberDevice] = useState(true);

  // Appeal states
  const [showAppealForm, setShowAppealForm] = useState(false);
  const [banReason, setBanReason] = useState('');
  const [appealReason, setAppealReason] = useState('');
  const [appealLoading, setAppealLoading] = useState(false);
  const [appealSuccess, setAppealSuccess] = useState('');
  const [appealError, setAppealError] = useState('');

  // Forgot password/username states
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotType, setForgotType] = useState('password'); // 'password' or 'username'
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState('');
  const [forgotError, setForgotError] = useState('');
  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setForgotError('');
    setForgotSuccess('');
    setForgotLoading(true);

    try {
      if (forgotType === 'username') {
        const response = await axios.post(`${API}/auth/forgot-username`, { email: forgotEmail });
        setForgotSuccess(response.data.message);
      } else {
        const response = await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
        setForgotSuccess(response.data.message);
      }
    } catch (err) {
      setForgotError(err.response?.data?.detail || 'An error occurred. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const loginData = {
        password: formData.password,
        login_type: loginType,
        device_token: localStorage.getItem('gj_device_token') || null,
        ...(loginType === 'email' 
          ? { email: formData.email } 
          : { username: formData.username })
      };
      
      const response = await axios.post(`${API}/auth/login`, loginData);
      
      // Check if OTP is required
      if (response.data.otp_required) {
        setOtpUserId(response.data.user_id);
        setOtpLoginToken(response.data.login_token);
        setOtpMaskedPhone(response.data.masked_phone);
        setOtpStep(true);
        setOtpError('');
        // Auto-send OTP
        handleSendOTP(response.data.user_id, response.data.login_token);
        return;
      }
      
      onLogin(response.data.user, response.data.access_token);
      navigate('/dashboard');
    } catch (error) {
      const isNetworkError = !error.response;
      const errorDetail = error.response?.data?.detail
        || (isNetworkError
          ? 'Cannot reach the server. Please check your internet connection — or the app may be pointing to the wrong server address.'
          : 'Login failed. Please try again.');

      // Check if the error is about being banned
      if (error.response?.status === 403 && errorDetail.toLowerCase().includes('banned')) {
        const reasonMatch = errorDetail.match(/Reason: (.+?)\. Please/);
        setBanReason(reasonMatch ? reasonMatch[1] : 'Account has been banned');
        setShowAppealForm(true);
        setError('');
      } else {
        setError(errorDetail);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSendOTP = async (userId, loginToken) => {
    setOtpSending(true);
    setOtpError('');
    try {
      const res = await axios.post(`${API}/otp/send`, {
        user_id: userId || otpUserId,
        login_token: loginToken || otpLoginToken,
      });
      setOtpMaskedPhone(res.data.masked_phone || otpMaskedPhone);
      // Start cooldown
      setOtpResendCooldown(30);
      const interval = setInterval(() => {
        setOtpResendCooldown(prev => {
          if (prev <= 1) { clearInterval(interval); return 0; }
          return prev - 1;
        });
      }, 1000);
    } catch (err) {
      setOtpError(err.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setOtpSending(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setOtpVerifying(true);
    setOtpError('');
    try {
      // Verify OTP
      await axios.post(`${API}/otp/verify`, {
        user_id: otpUserId,
        login_token: otpLoginToken,
        otp_code: otpCode,
      });
      // Complete login
      const response = await axios.post(`${API}/auth/otp-complete`, {
        user_id: otpUserId,
        login_token: otpLoginToken,
        remember_device: rememberDevice,
      });
      // Persist the trusted-device token so this browser can skip OTP for 30 days
      if (response.data.device_token) {
        localStorage.setItem('gj_device_token', response.data.device_token);
      }
      onLogin(response.data.user, response.data.access_token);
      navigate('/dashboard');
    } catch (err) {
      setOtpError(err.response?.data?.detail || 'OTP verification failed');
    } finally {
      setOtpVerifying(false);
    }
  };

  const handleAppealSubmit = async (e) => {
    e.preventDefault();
    setAppealError('');
    setAppealSuccess('');
    setAppealLoading(true);

    try {
      const response = await axios.post(`${API}/appeals/submit`, {
        email: formData.email,
        reason: appealReason
      });
      setAppealSuccess(response.data.message);
      setAppealReason('');
    } catch (error) {
      setAppealError(error.response?.data?.detail || 'Failed to submit appeal. Please try again.');
    } finally {
      setAppealLoading(false);
    }
  };

  // OTP Verification Screen
  if (otpStep) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4" data-testid="otp-page">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
              <Smartphone className="h-8 w-8 text-blue-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900">Verify Your Identity</h2>
            <p className="mt-2 text-gray-500">
              We sent a verification code to <span className="font-semibold">{otpMaskedPhone}</span>
            </p>
          </div>

          <div className="bg-white py-8 px-8 rounded-2xl shadow-sm border border-gray-100">
            {otpError && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm" data-testid="otp-error">
                {otpError}
              </div>
            )}

            <form onSubmit={handleVerifyOTP} data-testid="otp-form">
              <div className="mb-6">
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Enter 6-digit OTP code
                </label>
                <input
                  type="text"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="w-full h-14 px-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition text-center text-2xl tracking-[0.5em] font-mono"
                  placeholder="000000"
                  maxLength={6}
                  autoFocus
                  data-testid="otp-code-input"
                />
                <p className="text-xs text-gray-400 mt-2 text-center">Code expires in 5 minutes</p>
              </div>

              <label className="flex items-center gap-2 mb-5 cursor-pointer select-none" data-testid="remember-device-label">
                <input
                  type="checkbox"
                  checked={rememberDevice}
                  onChange={(e) => setRememberDevice(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  data-testid="remember-device-checkbox"
                />
                <span className="text-sm text-gray-600">Remember this device for 30 days (skip OTP on this browser)</span>
              </label>

              <button
                type="submit"
                disabled={otpVerifying || otpCode.length !== 6}
                className="w-full h-12 bg-gray-900 text-white rounded-full hover:bg-gray-800 font-medium disabled:bg-gray-400 disabled:cursor-not-allowed transition"
                data-testid="otp-verify-button"
              >
                {otpVerifying ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Verifying...
                  </span>
                ) : 'Verify & Sign In'}
              </button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-500 mb-2">Didn&apos;t receive the code?</p>
              <button
                onClick={() => handleSendOTP()}
                disabled={otpSending || otpResendCooldown > 0}
                className="text-sm text-blue-600 hover:text-blue-700 font-semibold disabled:text-gray-400 disabled:cursor-not-allowed transition"
                data-testid="otp-resend-button"
              >
                {otpSending ? 'Sending...' : otpResendCooldown > 0 ? `Resend in ${otpResendCooldown}s` : 'Resend Code'}
              </button>
            </div>

            <button
              onClick={() => { setOtpStep(false); setOtpCode(''); setOtpError(''); }}
              className="mt-4 w-full h-12 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 font-medium transition flex items-center justify-center gap-2"
              data-testid="otp-back-button"
            >
              <ArrowLeft className="h-5 w-5" />
              {t('auth.backToLogin')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Appeal Form View
  if (showAppealForm) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4" data-testid="appeal-page">
        <div className="max-w-md w-full">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
              <AlertTriangle className="h-8 w-8 text-red-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900">Account Banned</h2>
            <p className="mt-2 text-gray-500">Your account has been suspended</p>
          </div>

          {/* Ban Info Card */}
          <div className="bg-white py-8 px-8 rounded-2xl shadow-sm border border-gray-100">
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
              <p className="text-red-700 text-sm font-medium mb-1">Ban Reason:</p>
              <p className="text-red-600 text-sm">{banReason}</p>
            </div>

            {appealSuccess ? (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl" data-testid="appeal-success">
                <p className="text-green-700 text-sm font-medium mb-1">✓ Appeal Submitted</p>
                <p className="text-green-600 text-sm">{appealSuccess}</p>
                <p className="text-green-500 text-xs mt-2">You will be notified once an administrator reviews your appeal.</p>
              </div>
            ) : (
              <>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Submit an Appeal</h3>
                <p className="text-gray-500 text-sm mb-4">
                  If you believe this ban was made in error or have changed circumstances, you can submit an appeal for review.
                </p>

                {appealError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm" data-testid="appeal-error">
                    {appealError}
                  </div>
                )}

                <form onSubmit={handleAppealSubmit} data-testid="appeal-form">
                  <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-medium mb-2">
                      Your Email
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      disabled
                      className="w-full h-12 px-4 border border-gray-200 rounded-xl bg-gray-50 text-gray-500"
                    />
                  </div>

                  <div className="mb-6">
                    <label className="block text-gray-700 text-sm font-medium mb-2" htmlFor="appealReason">
                      Reason for Appeal *
                    </label>
                    <textarea
                      id="appealReason"
                      value={appealReason}
                      onChange={(e) => setAppealReason(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900 transition resize-none"
                      placeholder="Please explain why you believe your account should be reinstated..."
                      rows={4}
                      required
                      minLength={20}
                      data-testid="appeal-reason-input"
                    />
                    <p className="text-xs text-gray-400 mt-1">Minimum 20 characters</p>
                  </div>

                  <button
                    type="submit"
                    disabled={appealLoading || appealReason.length < 20}
                    className="w-full h-12 bg-orange-500 text-white rounded-full hover:bg-orange-600 font-medium disabled:bg-gray-400 disabled:cursor-not-allowed transition flex items-center justify-center gap-2"
                    data-testid="appeal-submit-button"
                  >
                    {appealLoading ? (
                      <>
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="h-5 w-5" />
                        Submit Appeal
                      </>
                    )}
                  </button>
                </form>
              </>
            )}

            <button
              onClick={() => {
                setShowAppealForm(false);
                setAppealSuccess('');
                setAppealError('');
                setAppealReason('');
              }}
              className="mt-4 w-full h-12 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 font-medium transition flex items-center justify-center gap-2"
              data-testid="back-to-login-button"
            >
              <ArrowLeft className="h-5 w-5" />
              {t('auth.backToLogin')}
            </button>
          </div>

          <p className="mt-8 text-center text-sm text-gray-400">
            {t('auth.needHelp')}
          </p>
        </div>
      </div>
    );
  }

  // Normal Login Form
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4" data-testid="login-page">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2" data-testid="login-logo">
            <img 
              src="/logo-dark.png" 
              alt="GoodJobPH" 
              className="h-12 w-12 object-contain"
            />
            <span className="text-2xl font-bold text-gray-900">GoodJob</span>
          </Link>
          <h2 className="mt-8 text-3xl font-bold text-gray-900" data-testid="login-title">{t('auth.welcomeBack')}</h2>
          <p className="mt-2 text-gray-500">{t('auth.signInToContinue')}</p>
        </div>

        {/* Form Card */}
        <div className="bg-white py-8 px-8 rounded-2xl shadow-sm border border-gray-100">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm" data-testid="login-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} data-testid="login-form">
            <div className="mb-5">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-gray-700 text-sm font-medium">
                  {loginType === 'email' ? t('auth.email') : t('auth.username')}
                </label>
                <button
                  type="button"
                  onClick={() => setLoginType(loginType === 'email' ? 'username' : 'email')}
                  className="text-xs text-orange-600 hover:text-orange-700 font-medium transition"
                  data-testid="login-type-toggle"
                >
                  {loginType === 'email' ? t('auth.useUsername') : t('auth.useEmail')}
                </button>
              </div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  {loginType === 'email' ? (
                    <Mail className="h-5 w-5 text-gray-400" />
                  ) : (
                    <User className="h-5 w-5 text-gray-400" />
                  )}
                </div>
                {loginType === 'email' ? (
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full h-12 pl-12 pr-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900 transition"
                    placeholder="you@example.com"
                    required
                    data-testid="login-email-input"
                  />
                ) : (
                  <input
                    type="text"
                    id="username"
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    className="w-full h-12 pl-12 pr-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900 transition"
                    placeholder="your_username"
                    required
                    data-testid="login-username-input"
                  />
                )}
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-gray-700 text-sm font-medium mb-2" htmlFor="password">
                {t('auth.password')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full h-12 pl-12 pr-12 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900 transition"
                  placeholder="••••••••"
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 transition"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Forgot Password/Username Links */}
            <div className="flex justify-between items-center text-sm">
              <button
                type="button"
                onClick={() => { setForgotType('username'); setShowForgotModal(true); setForgotSuccess(''); setForgotError(''); }}
                className="text-gray-600 hover:text-gray-900 transition"
                data-testid="forgot-username-link"
              >
                {t('auth.forgotUsername')}
              </button>
              <button
                type="button"
                onClick={() => { setForgotType('password'); setShowForgotModal(true); setForgotSuccess(''); setForgotError(''); }}
                className="text-gray-600 hover:text-gray-900 transition"
                data-testid="forgot-password-link"
              >
                {t('auth.forgotPassword')}
              </button>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-gray-900 text-white rounded-full hover:bg-gray-800 font-medium disabled:bg-gray-400 disabled:cursor-not-allowed transition"
              data-testid="login-submit-button"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  {t('auth.signingIn')}
                </span>
              ) : t('auth.signIn')}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-500">
              {t('auth.noAccount')}{' '}
              <Link to="/register" className="text-gray-900 hover:text-gray-700 font-semibold" data-testid="login-register-link">
                {t('auth.signUp')}
              </Link>
            </p>
          </div>
        </div>

        {/* Footer */}
        <p className="mt-8 text-center text-sm text-gray-400">
          {t('auth.agreeTerms')}
        </p>
      </div>

      {/* Forgot Password/Username Modal */}
      {showForgotModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6" data-testid="forgot-modal">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold">
                {forgotType === 'password' ? t('auth.resetPassword') : t('auth.recoverUsername')}
              </h3>
              <button
                onClick={() => { setShowForgotModal(false); setForgotEmail(''); setForgotSuccess(''); setForgotError(''); }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {forgotSuccess && (
              <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700" data-testid="forgot-success">
                {forgotSuccess}
              </div>
            )}

            {forgotError && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700" data-testid="forgot-error">
                {forgotError}
              </div>
            )}

            <form onSubmit={handleForgotSubmit}>
              <p className="text-gray-600 mb-4">
                {forgotType === 'password' 
                  ? t('auth.forgotPassDesc')
                  : t('auth.forgotUserDesc')}
              </p>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  {t('auth.emailAddress')}
                </label>
                <input
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  className="w-full h-11 px-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900"
                  placeholder={t('auth.enterEmail')}
                  required
                  data-testid="forgot-email-input"
                />
              </div>
              <button
                type="submit"
                disabled={forgotLoading}
                className="w-full h-11 bg-gray-900 text-white rounded-full hover:bg-gray-800 font-medium disabled:bg-gray-400 transition"
                data-testid="forgot-submit-btn"
              >
                {forgotLoading ? t('auth.processing') : (forgotType === 'password' ? t('auth.sendResetLink') : t('auth.recoverUsername'))}
              </button>
            </form>

            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => { setForgotType(forgotType === 'password' ? 'username' : 'password'); setForgotSuccess(''); setForgotError(''); }}
                className="text-sm text-gray-600 hover:text-gray-900"
              >
                {forgotType === 'password' ? t('auth.forgotUserInstead') : t('auth.forgotPassInstead')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginPage;
